// ============================================================
// src/pages/Earnings.jsx
//
// ── WHAT THIS PAGE DOES ──────────────────────────────────────
// Shows every gig as a spreadsheet row with its base pay,
// tips, and combined total. Filter by year, month, venue,
// city, booking type, or search by gig name. Every column
// header is sortable. A totals footer row always reflects the
// active filters. Each row has an Edit link. An Export button
// downloads the current filtered view as .xlsx via SheetJS.
// A Chart toggle shows a Recharts line chart of net earnings.
//
// ── MANAGER FEE SUPPORT ──────────────────────────────────────
// Stat cards show "You Keep" (net after manager cut) as the
// primary figure, alongside Gross and Manager Cut totals.
// Table has a "You Keep" column with the % shown when managed.
// "Booking" filter dropdown appears only when at least one
// managed gig exists, keeping the UI clean otherwise.
// Footer totals show the manager deduction breakdown.
// Chart reflects net earnings (what the performer keeps).
// Export includes Managed, Manager Fee %, Manager Cut, You Keep.
//
// ── DEPENDENCIES ─────────────────────────────────────────────
//   npm install xlsx
//   recharts (already in project)
//
// ── CSS CLASSES (all in index.css) ───────────────────────────
//   .earnings-page
//   .page-header-row / .page-header-btn-group
//   .earnings-stat-grid / .stat-card / .stat-label / .stat-value
//   .earnings-stat-icon
//   .earnings-filter-bar
//   .earnings-filter-label / .earnings-filter-group
//   .earnings-filter-field-label / .earnings-search-group
//   .earnings-select
//   .earnings-search-wrapper / .earnings-search-input
//   .earnings-search-clear / .earnings-clear-all-btn
//   .earnings-link-plain
//   .earnings-chart-wrapper
//   .earnings-chart-title / .earnings-chart-subtitle
//   .earnings-chart-empty
//   .earnings-tooltip / .earnings-tooltip-label / .earnings-tooltip-item
//   .earnings-empty-state / .earnings-empty-text
//   .earnings-table-outer / .earnings-table-scroll
//   .earnings-table
//   .earnings-thead-row / .earnings-th / .earnings-th-right
//   .earnings-th-center / .earnings-th-active
//   .earnings-sort-icon-inactive / .earnings-sort-icon-active
//   .earnings-tbody-row
//   .earnings-td / .earnings-td-text / .earnings-td-nowrap
//   .earnings-td-right / .earnings-td-bold / .earnings-td-center
//   .earnings-td-city
//   .earnings-td-tips-positive / .earnings-td-tips-zero
//   .earnings-td-total
//   .earnings-td-you-keep-managed / .earnings-td-you-keep-self
//   .earnings-td-you-keep-pct
//   .earnings-gig-link
//   .earnings-edit-btn
//   .earnings-badge / .earnings-badge-done
//   .earnings-badge-upcoming / .earnings-badge-pending
//   .earnings-managed-badge / .earnings-self-badge
//   .earnings-tfoot-row / .earnings-tfoot-label
//   .earnings-tfoot-managed-note / .earnings-tfoot-cell
//   .earnings-tfoot-tips / .earnings-tfoot-grand-total
//   .earnings-tfoot-you-keep / .earnings-tfoot-mgr-note
// ============================================================

import { useState, useMemo, useEffect } from 'react'
import { Link }                         from 'react-router-dom'
import { useGigs }                      from '../hooks/useGigs'
import { supabase }                     from '../lib/supabaseClient'
import * as XLSX from 'xlsx'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts'


// ── CSS VARIABLE RESOLVER ─────────────────────────────────────
// Recharts sets stroke/fill as SVG presentation attributes, not
// CSS properties, so CSS custom properties cannot be used as
// string values there directly. This helper resolves a CSS
// variable to its computed value at render time so Recharts
// receives a real colour string. The active theme is respected
// because a theme change triggers a re-render, causing these
// to re-evaluate.
const getCSSVar = (name) =>
  typeof window !== 'undefined'
    ? getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    : ''


// ── MONTH NAMES ───────────────────────────────────────────────
// Used in filter dropdowns and the export filename.
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]


// ── DATE HELPER ───────────────────────────────────────────────
// Parses a YYYY-MM-DD or ISO datetime string into a local Date
// without UTC offset shifting.
function parseLocalDate(dateStr) {
  const parts = String(dateStr).split('T')[0].split('-')
  return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
}


// ── CURRENCY FORMATTER ────────────────────────────────────────
// All money values displayed in the UI go through this function.
function fmt(num) {
  return Number(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}


// ── NET PAY HELPERS ───────────────────────────────────────────
// Tips always belong 100% to the performer, regardless of manager split.
// Manager cut is a percentage of the booking fee only (not tips).

function performerNet(gig) {
  const pay  = parseFloat(gig.payment) || 0
  const tips = parseFloat(gig.tips)    || 0
  if (gig.is_managed && gig.manager_fee_pct > 0) {
    const keepFraction = 1 - (parseFloat(gig.manager_fee_pct) / 100)
    return pay * keepFraction + tips
  }
  return pay + tips
}

function managerCut(gig) {
  const pay = parseFloat(gig.payment) || 0
  if (gig.is_managed && gig.manager_fee_pct > 0) {
    return pay * (parseFloat(gig.manager_fee_pct) / 100)
  }
  return 0
}


// ── STATUS BADGE ──────────────────────────────────────────────
// Defined outside Earnings so it is not re-created on every render.
function StatusBadge({ gig }) {
  if (gig.completed) {
    return (
      <span className="earnings-badge earnings-badge-done">
        <i className="bi bi-check-lg" aria-hidden="true" /> Done
      </span>
    )
  }
  const gigDate = parseLocalDate(gig.date)
  const today   = new Date()
  today.setHours(0, 0, 0, 0)
  const isUpcoming = gigDate >= today
  return (
    <span className={`earnings-badge ${isUpcoming ? 'earnings-badge-upcoming' : 'earnings-badge-pending'}`}>
      {isUpcoming
        ? <><i className="bi bi-star-fill" aria-hidden="true" /> Upcoming</>
        : <><i className="bi bi-hourglass-split" aria-hidden="true" /> Pending</>
      }
    </span>
  )
}


// ── MANAGED BADGE ─────────────────────────────────────────────
// Shows booking type. Self-booked gigs get a subtle "Self" label.
// Managed gigs get a pink pill with the performer's keep percentage.
function ManagedBadge({ gig }) {
  if (!gig.is_managed) {
    return <span className="earnings-self-badge">Self</span>
  }
  const feePct  = parseFloat(gig.manager_fee_pct) || 0
  const keepPct = 100 - feePct
  return (
    <span
      className="earnings-managed-badge"
      title={`Manager takes ${fmt(feePct)}%; you keep ${fmt(keepPct)}%`}
    >
      <i className="bi bi-person-badge-fill" aria-hidden="true" />
      Managed · You keep {fmt(keepPct)}%
    </span>
  )
}


// ── CHART TOOLTIP ─────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null
  return (
    <div className="earnings-tooltip">
      <p className="earnings-tooltip-label">{label}</p>
      {payload.map(entry => (
        <p key={entry.name} className="earnings-tooltip-item" style={{ color: entry.color }}>
          {entry.name}: <strong>${fmt(entry.value)}</strong>
        </p>
      ))}
    </div>
  )
}


// ── SORT ICON ─────────────────────────────────────────────────
function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) {
    return <i className="bi bi-arrow-down-up earnings-sort-icon-inactive" aria-hidden="true" />
  }
  return sortDir === 'asc'
    ? <i className="bi bi-arrow-up earnings-sort-icon-active" aria-hidden="true" />
    : <i className="bi bi-arrow-down earnings-sort-icon-active" aria-hidden="true" />
}


// ── EARNINGS PAGE ─────────────────────────────────────────────
function Earnings() {

  const { gigs, loading } = useGigs()

  // ── Filter state ──────────────────────────────────────────
  const [selectedYear,    setSelectedYear]    = useState('all')
  const [selectedMonth,   setSelectedMonth]   = useState('all')
  const [selectedVenue,   setSelectedVenue]   = useState('all')
  const [selectedCity,    setSelectedCity]    = useState('all')
  const [selectedManaged, setSelectedManaged] = useState('all') // 'all' | 'managed' | 'unmanaged'
  const [searchTerm,      setSearchTerm]      = useState('')

  // ── Sort state ────────────────────────────────────────────
  const [sortField, setSortField] = useState('date')
  const [sortDir,   setSortDir]   = useState('desc') // 'asc' | 'desc'

  // ── Chart visibility ──────────────────────────────────────
  const [showChart, setShowChart] = useState(false)

  // ── Performer name for export filename ───────────────────
  const [performerName, setPerformerName] = useState('Performer')

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      const name = user?.user_metadata?.performer_name
      if (name) setPerformerName(name)
    })
  }, [])


  // ── Derived filter options ────────────────────────────────

  const availableYears = useMemo(() => {
    const years = gigs.map(g => parseLocalDate(g.date).getFullYear())
    return [...new Set(years)].sort((a, b) => b - a)
  }, [gigs])

  const availableVenues = useMemo(() => {
    const venues = gigs.map(g => g.venue_name || '').filter(Boolean)
    return [...new Set(venues)].sort((a, b) => a.localeCompare(b))
  }, [gigs])

  const availableCities = useMemo(() => {
    const cities = gigs.map(g => g.city || '').filter(Boolean)
    return [...new Set(cities)].sort((a, b) => a.localeCompare(b))
  }, [gigs])

  // Only render the Booking filter when at least one managed gig exists
  const hasAnyManagedGigs = useMemo(
    () => gigs.some(g => g.is_managed),
    [gigs],
  )


  // ── Filtered + sorted gigs ────────────────────────────────

  const filteredGigs = useMemo(() => {
    return gigs.filter(gig => {
      const date = parseLocalDate(gig.date)

      const yearMatch =
        selectedYear === 'all' ||
        date.getFullYear() === parseInt(selectedYear)

      const monthMatch =
        selectedMonth === 'all' ||
        date.getMonth() === parseInt(selectedMonth)

      const venueMatch =
        selectedVenue === 'all' ||
        (gig.venue_name || '') === selectedVenue

      const cityMatch =
        selectedCity === 'all' ||
        (gig.city || '') === selectedCity

      const managedMatch =
        selectedManaged === 'all' ||
        (selectedManaged === 'managed'   &&  gig.is_managed) ||
        (selectedManaged === 'unmanaged' && !gig.is_managed)

      const searchMatch =
        searchTerm.trim() === '' ||
        (gig.name || gig.venue_name || '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase())

      return yearMatch && monthMatch && venueMatch && cityMatch && managedMatch && searchMatch
    })
  }, [gigs, selectedYear, selectedMonth, selectedVenue, selectedCity, selectedManaged, searchTerm])

  const sortedGigs = useMemo(() => {
    return [...filteredGigs].sort((a, b) => {
      let aVal, bVal

      if (sortField === 'date') {
        aVal = parseLocalDate(a.date).getTime()
        bVal = parseLocalDate(b.date).getTime()
      } else if (sortField === 'name') {
        aVal = (a.name || a.venue_name || '').toLowerCase()
        bVal = (b.name || b.venue_name || '').toLowerCase()
      } else if (sortField === 'venue') {
        aVal = (a.venue_name || '').toLowerCase()
        bVal = (b.venue_name || '').toLowerCase()
      } else if (sortField === 'payment') {
        aVal = parseFloat(a.payment) || 0
        bVal = parseFloat(b.payment) || 0
      } else if (sortField === 'tips') {
        aVal = parseFloat(a.tips) || 0
        bVal = parseFloat(b.tips) || 0
      } else if (sortField === 'total') {
        // Sort by gross (base + tips, before manager cut)
        aVal = (parseFloat(a.payment) || 0) + (parseFloat(a.tips) || 0)
        bVal = (parseFloat(b.payment) || 0) + (parseFloat(b.tips) || 0)
      } else if (sortField === 'net') {
        aVal = performerNet(a)
        bVal = performerNet(b)
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDir === 'asc' ?  1 : -1
      return 0
    })
  }, [filteredGigs, sortField, sortDir])


  // ── Totals ────────────────────────────────────────────────

  const totalPayment    = filteredGigs.reduce((s, g) => s + (parseFloat(g.payment) || 0), 0)
  const totalTips       = filteredGigs.reduce((s, g) => s + (parseFloat(g.tips)    || 0), 0)
  const totalGross      = totalPayment + totalTips
  const totalManagerCut = filteredGigs.reduce((s, g) => s + managerCut(g), 0)
  const totalNet        = filteredGigs.reduce((s, g) => s + performerNet(g), 0)
  const avgPerGig       = filteredGigs.length > 0 ? totalNet / filteredGigs.length : 0
  const completedCount  = filteredGigs.filter(g => g.completed).length
  const managedCount    = filteredGigs.filter(g => g.is_managed).length


  // ── Chart data ────────────────────────────────────────────
  // Groups filteredGigs by month. Chart shows performer NET,
  // Tips, Manager Cut (when relevant), and Gross.

  const chartData = useMemo(() => {
    const monthMap = {}

    filteredGigs.forEach(gig => {
      const date    = parseLocalDate(gig.date)
      const key     = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      const sortKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`

      if (!monthMap[key]) {
        monthMap[key] = { month: key, sortKey, basePay: 0, tips: 0, managerCut: 0, net: 0 }
      }

      monthMap[key].basePay    += parseFloat(gig.payment) || 0
      monthMap[key].tips       += parseFloat(gig.tips)    || 0
      monthMap[key].managerCut += managerCut(gig)
      monthMap[key].net        += performerNet(gig)
    })

    return Object.values(monthMap)
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
      .map(d => ({
        month:          d.month,
        'Net Earnings': parseFloat(d.net.toFixed(2)),
        'Tips':         parseFloat(d.tips.toFixed(2)),
        // Manager Cut line only when there is actually a manager cut in this dataset
        ...(totalManagerCut > 0 && {
          'Manager Cut': parseFloat(d.managerCut.toFixed(2)),
        }),
        'Gross': parseFloat((d.basePay + d.tips).toFixed(2)),
      }))
  }, [filteredGigs, totalManagerCut])


  // ── Event handlers ────────────────────────────────────────

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('asc')
    }
  }

  const clearAllFilters = () => {
    setSelectedYear('all')
    setSelectedMonth('all')
    setSelectedVenue('all')
    setSelectedCity('all')
    setSelectedManaged('all')
    setSearchTerm('')
  }

  const hasActiveFilters =
    selectedYear    !== 'all' ||
    selectedMonth   !== 'all' ||
    selectedVenue   !== 'all' ||
    selectedCity    !== 'all' ||
    selectedManaged !== 'all' ||
    searchTerm      !== ''


  // ── Export ────────────────────────────────────────────────

  const handleExport = () => {
    const rows = sortedGigs.map(gig => {
      const date    = parseLocalDate(gig.date)
      const payment = parseFloat(gig.payment) || 0
      const tips    = parseFloat(gig.tips)    || 0
      const gross   = payment + tips
      const cut     = managerCut(gig)
      const net     = performerNet(gig)
      const today   = new Date()
      today.setHours(0, 0, 0, 0)
      const status  = gig.completed
        ? 'Completed'
        : date >= today ? 'Upcoming' : 'Pending'

      return {
        'Gig Name':      gig.name || gig.venue_name || '',
        'Venue':         gig.venue_name  || '',
        'City':          gig.city        || '',
        'Date':          date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        'Base Pay':      payment,
        'Tips':          tips,
        'Gross Total':   gross,
        'Managed':       gig.is_managed ? 'Yes' : 'No',
        'Manager Fee %': gig.is_managed ? (parseFloat(gig.manager_fee_pct) || 0) : '',
        'Manager Cut':   gig.is_managed ? cut : '',
        'You Keep':      net,
        'Status':        status,
      }
    })

    // Totals row at the bottom of the sheet
    rows.push({
      'Gig Name':      `Totals (${filteredGigs.length} gig${filteredGigs.length !== 1 ? 's' : ''})`,
      'Venue': '', 'City': '', 'Date': '',
      'Base Pay':      totalPayment,
      'Tips':          totalTips,
      'Gross Total':   totalGross,
      'Managed':       `${managedCount} managed`,
      'Manager Fee %': '',
      'Manager Cut':   totalManagerCut,
      'You Keep':      totalNet,
      'Status':        '',
    })

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Earnings')

    const timeSpan = selectedYear !== 'all'
      ? selectedMonth !== 'all'
        ? `${MONTH_NAMES_SHORT[parseInt(selectedMonth)]}_${selectedYear}`
        : selectedYear
      : 'All'

    const cleanName = performerName.trim()
      .replace(/[^a-zA-Z0-9\s\-_]/g, '')
      .replace(/\s+/g, '_') || 'Performer'

    XLSX.writeFile(wb, `${cleanName}_${timeSpan}_dpp_earnings.xlsx`)
  }


  // ── Loading state ─────────────────────────────────────────

  if (loading) {
    return (
      <div className="earnings-page">
        <h1 className="page-title">Earnings</h1>
        <p className="earnings-loading-text">Loading your earnings...</p>
      </div>
    )
  }


  // ── Render ────────────────────────────────────────────────

  return (
    <div className="earnings-page">

      {/* A) PAGE HEADER */}
      <div className="page-header-row">
        <h1 className="page-title">Earnings</h1>
        <div className="page-header-btn-group">
          {/* btn base class required alongside variant for correct padding/radius */}
          <button
            className={`btn ${showChart ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowChart(prev => !prev)}
          >
            <i className="bi bi-graph-up-arrow" aria-hidden="true" />
            {' '}{showChart ? 'Hide Chart' : 'Chart'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleExport}
            disabled={filteredGigs.length === 0}
          >
            <i className="bi bi-file-earmark-excel" aria-hidden="true" /> Export
          </button>
          {/* Link wraps an anchor, not a button — avoids invalid a > button nesting */}
          <Link className="btn btn-primary earnings-link-plain" to="/gigs/new">
            + Add Gig
          </Link>
        </div>
      </div>


      {/* B) STAT CARDS */}
      {/* "You Keep" is first because it's the performer's actual take-home */}
      <div className="earnings-stat-grid">
        {[
          { label: 'You Keep',       value: `$${fmt(totalNet)}`,        colorCls: 'earnings-stat-value--keep',    icon: 'bi-wallet2'           },
          { label: 'Total Earnings', value: `$${fmt(totalGross)}`,      colorCls: 'earnings-stat-value--primary', icon: 'bi-currency-dollar'   },
          { label: 'Manager Cut',    value: `$${fmt(totalManagerCut)}`, colorCls: 'earnings-stat-value--neutral', icon: 'bi-person-badge-fill' },
          { label: 'Tips Earned',    value: `$${fmt(totalTips)}`,       colorCls: 'earnings-stat-value--primary', icon: 'bi-heart-fill'        },
          { label: 'Avg Per Gig',    value: `$${fmt(avgPerGig)}`,       colorCls: 'earnings-stat-value--primary', icon: 'bi-graph-up'          },
          { label: 'Gigs Shown',     value: filteredGigs.length,        colorCls: 'earnings-stat-value--primary', icon: 'bi-star-fill'         },
          { label: 'Managed Gigs',   value: managedCount,               colorCls: 'earnings-stat-value--accessible', icon: 'bi-person-badge-fill' },
          { label: 'Completed',      value: completedCount,             colorCls: 'earnings-stat-value--tertiary1',  icon: 'bi-check-circle-fill' },
        ].map(card => (
          <div key={card.label} className="stat-card">
            <p className="stat-label">
              <i className={`bi ${card.icon} earnings-stat-icon`} aria-hidden="true" />
              {card.label}
            </p>
            <p className={`stat-value ${card.colorCls}`}>{card.value}</p>
          </div>
        ))}
      </div>


      {/* C) FILTER BAR */}
      <div className="earnings-filter-bar">

        <span className="earnings-filter-label">
          <i className="bi bi-funnel-fill" aria-hidden="true" /> Filter:
        </span>

        {/* Year — resetting month on year change prevents impossible combos */}
        <div className="earnings-filter-group">
          <label className="earnings-filter-field-label" htmlFor="filter-year">Year:</label>
          <select
            id="filter-year"
            value={selectedYear}
            onChange={e => { setSelectedYear(e.target.value); setSelectedMonth('all') }}
            className="earnings-select"
          >
            <option value="all">All Years</option>
            {availableYears.map(year => (
              <option key={year} value={String(year)}>{year}</option>
            ))}
          </select>
        </div>

        {/* Month */}
        <div className="earnings-filter-group">
          <label className="earnings-filter-field-label" htmlFor="filter-month">Month:</label>
          <select
            id="filter-month"
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className="earnings-select"
          >
            <option value="all">All Months</option>
            {MONTH_NAMES.map((name, index) => (
              <option key={name} value={String(index)}>{name}</option>
            ))}
          </select>
        </div>

        {/* Venue */}
        {availableVenues.length > 0 && (
          <div className="earnings-filter-group">
            <label className="earnings-filter-field-label" htmlFor="filter-venue">Venue:</label>
            <select
              id="filter-venue"
              value={selectedVenue}
              onChange={e => setSelectedVenue(e.target.value)}
              className="earnings-select"
            >
              <option value="all">All Venues</option>
              {availableVenues.map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
        )}

        {/* City */}
        {availableCities.length > 0 && (
          <div className="earnings-filter-group">
            <label className="earnings-filter-field-label" htmlFor="filter-city">City:</label>
            <select
              id="filter-city"
              value={selectedCity}
              onChange={e => setSelectedCity(e.target.value)}
              className="earnings-select"
            >
              <option value="all">All Cities</option>
              {availableCities.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}

        {/* Booking type — only shown when at least one managed gig exists */}
        {hasAnyManagedGigs && (
          <div className="earnings-filter-group">
            <label className="earnings-filter-field-label" htmlFor="filter-booking">Booking:</label>
            <select
              id="filter-booking"
              value={selectedManaged}
              onChange={e => setSelectedManaged(e.target.value)}
              className="earnings-select"
            >
              <option value="all">All Gigs</option>
              <option value="managed">Managed Only</option>
              <option value="unmanaged">Self-Booked Only</option>
            </select>
          </div>
        )}

        {/* Search */}
        <div className="earnings-filter-group earnings-search-group">
          <label className="earnings-filter-field-label" htmlFor="filter-search">Gig:</label>
          <div className="earnings-search-wrapper">
            <input
              id="filter-search"
              type="text"
              placeholder="Search by gig name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="earnings-search-input"
            />
            {searchTerm && (
              <button
                className="earnings-search-clear"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                <i className="bi bi-x" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <button className="earnings-clear-all-btn" onClick={clearAllFilters}>
            <i className="bi bi-x-circle" aria-hidden="true" /> Clear All
          </button>
        )}
      </div>


      {/* D) CHART */}
      {showChart && (
        <div className="earnings-chart-wrapper">
          <p className="earnings-chart-title">
            <i className="bi bi-graph-up-arrow dash-section-icon" aria-hidden="true" />
            {' '}Net Earnings Over Time
          </p>
          <p className="earnings-chart-subtitle">
            Grouped by month, reflects active filters, shows what you keep after manager fees
          </p>

          {chartData.length === 0 ? (
            <div className="earnings-chart-empty">
              No data to chart for the current filters.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>

                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={`rgba(${getCSSVar('--secondary-rgb')}, 0.15)`}
                />

                <XAxis
                  dataKey="month"
                  tick={{ fill: 'var(--neutral-1)', fontSize: 11, opacity: 0.7 }}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={v => `$${v}`}
                  tick={{ fill: 'var(--neutral-1)', fontSize: 11, opacity: 0.7 }}
                  tickLine={false}
                  width={60}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: '0.78rem', paddingTop: '0.5rem' }} />

                {/* Net — primary brand colour, most prominent line */}
                <Line
                  type="monotone"
                  dataKey="Net Earnings"
                  stroke={getCSSVar('--primary')}
                  strokeWidth={3}
                  dot={{ r: 4, fill: getCSSVar('--primary'), strokeWidth: 0 }}
                  activeDot={{ r: 6 }}
                />

                {/* Tips — success green, dashed to distinguish from Net */}
                <Line
                  type="monotone"
                  dataKey="Tips"
                  stroke={getCSSVar('--tertiary-1')}
                  strokeWidth={2}
                  dot={{ r: 3, fill: getCSSVar('--tertiary-1'), strokeWidth: 0 }}
                  strokeDasharray="3 3"
                />

                {/* Manager Cut — muted purple, only rendered when relevant */}
                {totalManagerCut > 0 && (
                  <Line
                    type="monotone"
                    dataKey="Manager Cut"
                    stroke={getCSSVar('--secondary-light')}
                    strokeWidth={2}
                    dot={{ r: 3, fill: getCSSVar('--secondary-light'), strokeWidth: 0 }}
                    strokeDasharray="5 3"
                  />
                )}

              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}


      {/* E) TABLE OR EMPTY STATE */}
      {filteredGigs.length === 0 ? (

        <div className="earnings-empty-state">
          <p className="earnings-empty-text">
            {gigs.length === 0
              ? 'No gigs yet. Add your first one!'
              : 'No gigs match these filters. Try clearing them.'
            }
          </p>
          <Link className="btn btn-primary earnings-link-plain" to="/gigs/new">
            + Add a Gig
          </Link>
        </div>

      ) : (

        <div className="earnings-table-outer">
          <div className="earnings-table-scroll">
            <table className="earnings-table">

              {/* E1) HEADER */}
              <thead>
                <tr className="earnings-thead-row">

                  {[
                    { key: 'name',    label: 'Gig Name',    cls: 'earnings-th' },
                    { key: 'venue',   label: 'Venue',       cls: 'earnings-th' },
                    { key: 'date',    label: 'Date',        cls: 'earnings-th' },
                    { key: 'payment', label: 'Booking Fee', cls: 'earnings-th earnings-th-right' },
                    { key: 'tips',    label: 'Tips',        cls: 'earnings-th earnings-th-right' },
                    { key: 'net',     label: 'You Keep',    cls: 'earnings-th earnings-th-right' },
                    { key: 'total',   label: 'Gross',       cls: 'earnings-th earnings-th-right' },
                  ].map(col => (
                    <th
                      key={col.key}
                      className={`${col.cls}${sortField === col.key ? ' earnings-th-active' : ''}`}
                      onClick={() => handleSort(col.key)}
                    >
                      {col.label}{' '}
                      <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />
                    </th>
                  ))}

                  <th className="earnings-th earnings-th-center">Booking</th>
                  <th className="earnings-th earnings-th-center">Status</th>
                  <th className="earnings-th earnings-th-center">Edit</th>
                </tr>
              </thead>

              {/* E2) BODY */}
              <tbody>
                {sortedGigs.map(gig => {
                  const date    = parseLocalDate(gig.date)
                  const payment = parseFloat(gig.payment) || 0
                  const tips    = parseFloat(gig.tips)    || 0
                  const gross   = payment + tips
                  const net     = performerNet(gig)
                  const dateStr = date.toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                  })

                  return (
                    <tr key={gig.id} className="earnings-tbody-row">

                      <td className="earnings-td">
                        <Link to={`/gigs/${gig.id}`} className="earnings-gig-link">
                          {gig.name || gig.venue_name}
                        </Link>
                      </td>

                      <td className="earnings-td earnings-td-text">
                        <span>{gig.venue_name || 'N/A'}</span>
                        {gig.city && (
                          <span className="earnings-td-city">
                            {gig.city}{gig.state ? `, ${gig.state}` : ''}
                          </span>
                        )}
                      </td>

                      <td className="earnings-td earnings-td-nowrap">{dateStr}</td>

                      <td className="earnings-td earnings-td-right earnings-td-bold">
                        ${fmt(payment)}
                      </td>

                      <td className={`earnings-td earnings-td-right ${tips > 0 ? 'earnings-td-tips-positive' : 'earnings-td-tips-zero'}`}>
                        {tips > 0 ? `+$${fmt(tips)}` : 'None'}
                      </td>

                      {/* You Keep — green-ish for managed (cut happened), pink for self */}
                      <td className={`earnings-td earnings-td-right ${gig.is_managed ? 'earnings-td-you-keep-managed' : 'earnings-td-you-keep-self'}`}>
                        ${fmt(net)}
                        {gig.is_managed && gig.manager_fee_pct > 0 && (
                          <span className="earnings-td-you-keep-pct">
                            ({fmt(100 - parseFloat(gig.manager_fee_pct))}%)
                          </span>
                        )}
                      </td>

                      <td className="earnings-td earnings-td-right earnings-td-total">
                        ${fmt(gross)}
                      </td>

                      <td className="earnings-td earnings-td-center">
                        <ManagedBadge gig={gig} />
                      </td>

                      <td className="earnings-td earnings-td-center">
                        <StatusBadge gig={gig} />
                      </td>

                      {/* Link styled as a button — avoids the invalid <Link><button> nesting */}
                      <td className="earnings-td earnings-td-center">
                        <Link
                          to={`/gigs/edit/${gig.id}`}
                          className="earnings-edit-btn earnings-link-plain"
                        >
                          <i className="bi bi-pencil-fill" aria-hidden="true" /> Edit
                        </Link>
                      </td>

                    </tr>
                  )
                })}
              </tbody>

              {/* E3) FOOTER */}
              <tfoot>
                <tr className="earnings-tfoot-row">
                  <td colSpan={3} className="earnings-tfoot-label">
                    Totals: {filteredGigs.length} gig{filteredGigs.length !== 1 ? 's' : ''}
                    {managedCount > 0 && (
                      <span className="earnings-tfoot-managed-note">
                        {' '}{managedCount} managed
                      </span>
                    )}
                  </td>
                  <td className="earnings-tfoot-cell">${fmt(totalPayment)}</td>
                  <td className="earnings-tfoot-cell earnings-tfoot-tips">+${fmt(totalTips)}</td>
                  <td className="earnings-tfoot-cell earnings-tfoot-you-keep">
                    ${fmt(totalNet)}
                    {totalManagerCut > 0 && (
                      <span className="earnings-tfoot-mgr-note">
                        ({fmt(totalManagerCut)} mgr cut)
                      </span>
                    )}
                  </td>
                  <td className="earnings-tfoot-cell earnings-tfoot-grand-total">
                    ${fmt(totalGross)}
                  </td>
                  <td /><td /><td />
                </tr>
              </tfoot>

            </table>
          </div>
        </div>
      )}

    </div>
  )
}

export default Earnings